import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useConfirm } from '../components/ConfirmDialog';
import CustomFiltersMenu from '../components/CustomFiltersMenu';
import { matchesAllRules } from '../lib/projectFilterRules';
import KanbanBoard from '../components/KanbanBoard';
import TaskListView from '../components/TaskListView';
import ProjectOverview from '../components/ProjectOverview';
import BacklogView from '../components/BacklogView';
import PagesView from '../components/PagesView';
import TimelineView from '../components/TimelineView';
import WhiteboardView from '../components/WhiteboardView';
import { UpgradeLockCard } from '../components/UpgradeLock';
import TaskSheet from '../components/TaskSheet';
import CreateTaskDialog from '../components/CreateTaskDialog';
import ImportExportTasksDialog from '../components/ImportExportTasksDialog';
import BulkActionsBar from '../components/BulkActionsBar';
import { Button } from '../components/ui/button';
import { ClientLogo } from '../components/ClientLogo';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Kanban, ListBullets, Plus, FunnelSimple, SortAscending, SquaresFour, BookOpen, GitBranch, PencilSimple,
  MagnifyingGlass, GearSix, Star, X, ChartBar, Lightning, Columns, Bug, FileDashed, Check,
  DownloadSimple,
} from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { WorkTypeIcon } from '../components/KanbanBoard';

// Quick-filter panel building blocks (checkbox rows grouped by dimension).
function FilterGroup({ title, children }) {
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden bg-white">
      <div className="px-3 py-1.5 bg-zinc-50/60 border-b border-zinc-100">
        <p className="text-[11px] font-bold text-zinc-600">{title}</p>
      </div>
      <div className="p-1.5 flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function CheckRow({ checked, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-left hover:bg-zinc-50 transition-colors ${checked ? 'bg-blue-50/50' : ''}`}
    >
      <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border flex-shrink-0 ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-zinc-300'}`}>
        {checked && <Check size={9} weight="bold" />}
      </span>
      <span className="flex-1 truncate text-zinc-700">{label}</span>
    </button>
  );
}

// Jira-style quick assignee filter: a horizontal row of member avatars right
// on the board toolbar. Click an avatar to toggle filtering by that assignee.
// Overflow members collapse into a searchable "+N" popover.
function AssigneeQuickFilter({ members, selected, onToggle, onClear }) {
  const [q, setQ] = useState('');
  const MAX = 6;
  const shown = members.slice(0, MAX);
  const rest = members.slice(MAX);
  const restFiltered = rest.filter(m => (m.name || m.email || '').toLowerCase().includes(q.toLowerCase()));
  const restActive = rest.some(m => selected.includes(m.user_id));
  const AvatarChip = ({ m }) => {
    const s = getAvatarStyle(m.name || m.email);
    const active = selected.includes(m.user_id);
    return (
      <button
        type="button"
        title={m.name || m.email}
        onClick={() => onToggle(m.user_id)}
        data-testid={`board-assignee-filter-${m.user_id}`}
        className={`rounded-full transition-transform hover:-translate-y-0.5 ${active ? 'ring-2 ring-blue-600 ring-offset-1' : 'ring-1 ring-white hover:ring-zinc-300'}`}
      >
        <Avatar className="h-6 w-6"><AvatarImage src={m.avatar} /><AvatarFallback className={`${s.bg} text-white text-[9px] font-bold`}>{getInitials(m.name || m.email)}</AvatarFallback></Avatar>
      </button>
    );
  };
  return (
    <div className="flex items-center gap-1" data-testid="board-assignee-quickfilter">
      {shown.map(m => <AvatarChip key={m.user_id} m={m} />)}
      {rest.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="board-assignee-filter-more"
              className={`h-6 min-w-[24px] px-1.5 rounded-full text-[10px] font-semibold transition-colors ${restActive ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-600' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
            >+{rest.length}</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2">
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search people…" className="h-7 text-[11px] mb-2" data-testid="board-assignee-filter-search" />
            <div className="max-h-56 overflow-y-auto flex flex-col gap-0.5">
              {restFiltered.length === 0 && <p className="text-[11px] text-zinc-400 py-1 px-2">No people</p>}
              {restFiltered.map(m => (
                <CheckRow key={m.user_id} testid={`board-assignee-filter-more-${m.user_id}`} checked={selected.includes(m.user_id)} onClick={() => onToggle(m.user_id)} label={m.name || m.email} />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      {selected.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          data-testid="board-assignee-filter-clear"
          className="ml-0.5 h-6 px-2 rounded-full text-[10px] text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
        >Clear</button>
      )}
    </div>
  );
}



export default function ProjectBoardPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const { hasCap, flagSource } = useSubscription() || {};
  const confirm = useConfirm();
  const csvUnlocked = hasCap?.('csv_export') || hasCap?.('csv_import');
  const docsUnlocked = hasCap?.('docs');
  const whiteboardUnlocked = hasCap?.('whiteboard');
  const timelineUnlocked = hasCap?.('timeline');
  const [project, setProject] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [projectSprints, setProjectSprints] = useState([]);
  const [projectLinks, setProjectLinks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [view, setView] = useState('kanban');

  // If the current view's feature was just admin-disabled (e.g. SA toggled off
  // while the tab was open), bounce the user to Summary so they're not staring
  // at a missing-tab state.
  useEffect(() => {
    if (view === 'docs' && flagSource?.('docs') === 'admin_disabled') setView('summary');
    else if (view === 'whiteboard' && flagSource?.('whiteboard') === 'admin_disabled') setView('summary');
    else if (view === 'timeline' && flagSource?.('timeline') === 'admin_disabled') setView('summary');
  }, [view, flagSource]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [createDefaultStatus, setCreateDefaultStatus] = useState('todo');
  const [createSprintId, setCreateSprintId] = useState(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  // Tracks any task-list mutation. BacklogView watches this to know when the
  // parent's CreateTaskDialog (or anything else) has produced a new/updated
  // task so it can re-fetch its sprint groupings — otherwise the new ticket
  // would only appear after a hard refresh.
  const [taskListVersion, setTaskListVersion] = useState(0);
  const bumpTaskList = useCallback(() => setTaskListVersion(v => v + 1), []);

  // Bulk-select state. A Set of task_ids; bulk-actions bar appears when size > 0.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Quick filters — multi-select arrays (Jira-style). Within a group, values
  // combine with OR; across groups they combine with AND. All applied
  // client-side so multiple values work without server round-trips.
  const [filterPriorities, setFilterPriorities] = useState([]);
  const [filterAssignees, setFilterAssignees] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterTags, setFilterTags] = useState([]);

  // Saved (custom) filters. `savedFilters` is the project's filter library;
  // `appliedFilterIds` are the ones ticked on the board (combined with AND).
  const [savedFilters, setSavedFilters] = useState([]);
  const [appliedFilterIds, setAppliedFilterIds] = useState([]);
  // Workspace timezone — drives absolute date-range filter comparisons.
  const [orgTimezone, setOrgTimezone] = useState('UTC');

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const fetchProject = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/projects/${projectId}`, h()); setProject(data); } catch {}
  }, [projectId, currentOrg, API, h]);

  const fetchTasks = useCallback(async () => {
    if (!currentOrg) return;
    // Fetch the full project task list; all filtering happens client-side so
    // multi-select quick filters and saved-filter rules apply without refetch.
    try { const { data } = await axios.get(`${API}/projects/${projectId}/tasks`, h()); setTasks(data); return data; } catch { return null; }
  }, [projectId, currentOrg, API, h]);

  const fetchSavedFilters = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/projects/${projectId}/filters`, h()); setSavedFilters(data); } catch {}
  }, [currentOrg, projectId, API, h]);

  const fetchTimezone = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/organizations/current/settings`, h()); if (data?.timezone) setOrgTimezone(data.timezone); } catch {}
  }, [currentOrg, API, h]);

  const fetchMembers = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/projects/${projectId}/members`, h()); setMembers(data); } catch {}
  }, [currentOrg, projectId, API, h]);

  useEffect(() => { fetchProject(); fetchTasks(); fetchMembers(); fetchSavedFilters(); fetchTimezone(); }, [fetchProject, fetchTasks, fetchMembers, fetchSavedFilters, fetchTimezone]);

  // Reset all board filters when switching projects. The board component is
  // reused across /projects/:projectId routes (React Router doesn't remount on
  // param change), so without this, filters from one project would leak onto
  // the next — including assignee ids that don't exist in the new project.
  useEffect(() => {
    setFilterStatuses([]); setFilterTypes([]); setFilterPriorities([]); setFilterAssignees([]); setFilterTags([]);
    setAppliedFilterIds([]);
  }, [projectId]);

  // Fetch available templates once for the "What's included" tooltip
  useEffect(() => {
    if (!currentOrg) return;
    axios.get(`${API}/projects/templates`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setTemplates(r.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  // Fetch the project's sprints so we know whether to show the Backlog tab.
  useEffect(() => {
    if (!projectId || !currentOrg) return;
    axios.get(`${API}/projects/${projectId}/sprints`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setProjectSprints(r.data?.sprints || []))
      .catch(() => setProjectSprints([]));
    // Project-scoped task_links for Timeline dependency arrows.
    axios.get(`${API}/projects/${projectId}/links`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setProjectLinks(r.data || []))
      .catch(() => setProjectLinks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentOrg]);

  // Optimistic update: patch local tasks state immediately so the Kanban
  // card settles into the target column without snapping back while the
  // API round-trip completes. On failure we revert and re-fetch.
  // Done-guardrail. Surfaces a soft warning when the user moves a card to
  // `done` while EITHER:
  //   • a still-open `is_blocked_by` link points at the task, OR
  //   • there are open subtasks (parent_task_id === this.task_id).
  // Both checks run in parallel so we don't double the latency on large
  // backlogs. A failure of either fetch falls through silently — we never
  // hard-block the move on a transient network error.
  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    if (newStatus === 'done') {
      try {
        const [linksRes, subtasksRes] = await Promise.allSettled([
          axios.get(`${API}/tasks/${taskId}/links`, h()),
          axios.get(`${API}/tasks/${taskId}/subtasks`, h()),
        ]);
        const links = linksRes.status === 'fulfilled' ? (linksRes.value.data || []) : [];
        const subtasks = subtasksRes.status === 'fulfilled' ? (subtasksRes.value.data || []) : [];
        const openBlockers = links.filter(l => l.link_type === 'is_blocked_by'
          && l.target?.status && l.target.status !== 'done' && !l.target.is_deleted);
        const openSubtasks = subtasks.filter(s => s.status !== 'done' && !s.is_deleted);

        if (openBlockers.length > 0 || openSubtasks.length > 0) {
          const lines = [];
          if (openSubtasks.length > 0) {
            const list = openSubtasks.slice(0, 3).map(s => s.task_key).join(', ')
              + (openSubtasks.length > 3 ? `, +${openSubtasks.length - 3} more` : '');
            lines.push(`${openSubtasks.length} subtask${openSubtasks.length === 1 ? '' : 's'} still open: ${list}.`);
          }
          if (openBlockers.length > 0) {
            const list = openBlockers.slice(0, 3).map(l => l.target.task_key).join(', ')
              + (openBlockers.length > 3 ? `, +${openBlockers.length - 3} more` : '');
            lines.push(`Blocked by: ${list}.`);
          }
          const ok = await confirm({
            title: 'Open work detected',
            description: `${lines.join(' ')} Mark this task as Done anyway?`,
            confirmText: 'Done anyway',
            cancelText: 'Keep open',
            variant: 'destructive',
          });
          if (!ok) return; // user backed out — leave the card where it was
        }
      } catch (err) {
        console.warn('[done-guard] failed', err);
      }
    }
    const prev = tasks;
    setTasks(tasks.map(t => t.task_id === taskId ? { ...t, status: newStatus } : t));
    try {
      await axios.patch(`${API}/tasks/${taskId}/status`, { status: newStatus }, h());
    } catch (err) {
      console.error('Status update failed', err);
      setTasks(prev);
      fetchTasks();
    }
  };

  // Generic field update for inline-edit chips on Kanban cards (assignee, due date, custom_fields etc.)
  const handleTaskFieldUpdate = async (taskId, patch) => {
    const prev = tasks;
    // Optimistic update — instant feedback, will be replaced by the server
    // response below.
    setTasks(tasks.map(t => t.task_id === taskId ? { ...t, ...patch } : t));
    try {
      // IMPORTANT: capture the response. The backend may auto-recalculate
      // fields based on `patch` (e.g. updating `custom_fields.story_points`
      // triggers a recompute of `original_estimate_hours` and
      // `remaining_hours` using the project's hours_per_story_point ratio).
      // Without merging the response, the card chip and any sibling stats
      // (Estimate, Remaining) stay stale until the user navigates away
      // and back. This is the fix for the bug where Estimate didn't
      // update when SP was changed from the Kanban card.
      const { data: server } = await axios.put(`${API}/tasks/${taskId}`, patch, h());
      if (server && server.task_id) {
        setTasks(curr => curr.map(t => t.task_id === taskId ? { ...t, ...server } : t));
      }
    } catch (err) {
      console.error('Task update failed', err);
      setTasks(prev);
      fetchTasks();
    }
  };

  const handleAddColumn = async (name, color) => {
    try {
      await axios.post(`${API}/projects/${projectId}/statuses`, { name, color }, h());
      fetchProject();
    } catch (err) { console.error(err); }
  };

  const handleDeleteColumn = async (statusId) => {
    if (!(await confirm({ title: 'Delete this column?', description: 'All tasks in this column will be moved to To Do. This cannot be undone.', confirmText: 'Delete column', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/statuses/${statusId}`, h());
      fetchProject();
      fetchTasks();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete column'); }
  };

  const handleRenameColumn = async (statusId, name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    try {
      await axios.patch(`${API}/projects/${projectId}/statuses/${statusId}`, { name: clean }, h());
      fetchProject();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to rename column'); }
  };

  const getMemberName = (uid) => members.find(u => u.user_id === uid)?.name || 'Unassigned';
  const getMemberAvatar = (uid) => members.find(u => u.user_id === uid)?.avatar || null;
  const canEdit = currentOrg?.role !== 'viewer';
  // Project Settings, member admin, custom field/status edits are off-limits
  // to contributors and viewers — they're work-on-tasks roles, not project
  // admin roles. Backend mirrors this with requireOrgRoles on those endpoints.
  const canManageProject = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);

  const handleQuickAdd = (status) => { setCreateDefaultStatus(status); setShowCreateTask(true); };

  // Apply dimension dropdowns first, then saved custom filter rules, then
  // finally the search box. Any dimension with `all` is a no-op.
  // Quick filters: within a group match ANY selected value; empty group = no-op.
  const applyDimensionFilters = (arr) => arr.filter(t => (
    (filterPriorities.length === 0 || filterPriorities.includes(t.priority)) &&
    (filterAssignees.length === 0 || (t.assignee_id && filterAssignees.includes(t.assignee_id))) &&
    (filterStatuses.length === 0 || filterStatuses.includes(t.status)) &&
    (filterTypes.length === 0 || filterTypes.includes(t.work_type || 'task')) &&
    (filterTags.length === 0 || (t.tags || []).some(tag => filterTags.includes(tag)))
  ));
  const appliedFilters = savedFilters.filter(f => appliedFilterIds.includes(f.filter_id));
  const applyCustomFilter = (arr) => {
    if (appliedFilters.length === 0) return arr;
    const rulesList = appliedFilters.map(f => f.rules || {});
    return arr.filter(t => matchesAllRules(t, rulesList, { timezone: orgTimezone }));
  };
  const applySearch = (arr) => !searchQuery ? arr : arr.filter(t =>
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.task_key?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // Scrum-style board: the Kanban only shows tasks belonging to the
  // currently active sprint. Backlog tickets (no sprint, or in a future
  // sprint) only appear under List → Backlog. If no sprint is active the
  // board renders an empty state ("No active sprint") so teams remember
  // to start a sprint before working off the board.
  const activeSprint = projectSprints.find(s => s.status === 'active') || null;
  const applyBoardScope = (arr) => {
    if (view !== 'kanban') return arr;
    if (!activeSprint) return [];
    return arr.filter(t => t.sprint_id === activeSprint.sprint_id);
  };
  const filteredTasks = applySearch(applyCustomFilter(applyDimensionFilters(applyBoardScope(tasks))));

  const assigneeIds = [...new Set(tasks.map(t => t.assignee_id).filter(Boolean))];
  const assigneeAvatars = assigneeIds.slice(0, 4).map(id => ({ id, name: getMemberName(id), avatar: getMemberAvatar(id) }));

  const activeFilterCount = filterPriorities.length + filterAssignees.length + filterStatuses.length + filterTypes.length + filterTags.length;

  const clearFilters = () => { setFilterPriorities([]); setFilterAssignees([]); setFilterStatuses([]); setFilterTypes([]); setFilterTags([]); };
  const toggleArrVal = (setter) => (val) => setter(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleAppliedFilter = (id) => setAppliedFilterIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Template badge metadata
  const TEMPLATE_META = {
    blank:       { icon: FileDashed, label: 'Blank',       cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
    scrum:       { icon: Lightning,  label: 'Scrum',       cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    kanban:      { icon: Columns,    label: 'Kanban',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    bug_tracker: { icon: Bug,        label: 'Bug Tracker', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  };
  const activeTemplateId = project?.template && TEMPLATE_META[project.template] ? project.template : null;
  const activeTemplate = activeTemplateId ? templates.find(t => t.id === activeTemplateId) : null;

  const handleReorder = async (taskIds) => {
    const prev = tasks;
    // Optimistic: re-sort local tasks array according to provided ids.
    const idOrder = new Map(taskIds.map((id, i) => [id, i]));
    const reordered = [...tasks].sort((a, b) => {
      const ai = idOrder.has(a.task_id) ? idOrder.get(a.task_id) : 1e9;
      const bi = idOrder.has(b.task_id) ? idOrder.get(b.task_id) : 1e9;
      return ai - bi;
    });
    // Update order field locally so Kanban re-renders in the new position.
    const withOrder = reordered.map((t, i) => idOrder.has(t.task_id) ? { ...t, order: i } : t);
    setTasks(withOrder);
    try { await axios.patch(`${API}/projects/${projectId}/tasks/reorder`, { task_ids: taskIds }, h()); }
    catch (err) { console.error('Reorder failed', err); setTasks(prev); fetchTasks(); }
  };

  // Persist board column order. KanbanBoard owns the drag UX and just hands
  // us back the new array of status IDs — we save it on the project so the
  // ordering survives a refresh.
  const handleReorderColumns = async (statusIds) => {
    setProject(prev => prev ? { ...prev, status_order: statusIds } : prev);
    try {
      await axios.patch(`${API}/projects/${projectId}/status-order`, { status_ids: statusIds }, h());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save column order');
      fetchProject();
    }
  };

  // Build status options from default + custom
  const allStatuses = [
    { value: 'todo', label: 'To Do' }, { value: 'in_progress', label: 'In Progress' },
    { value: 'review', label: 'Review' }, { value: 'done', label: 'Done' },
    ...(project?.custom_statuses || []).map(s => ({ value: s.id, label: s.label })),
  ];
  const boardTags = [...new Set(tasks.flatMap(t => t.tags || []))].sort();

  // ─── Bulk-select helpers ────────────────────────────────────
  const toggleSelect = (taskId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  const toggleSelectAll = (checked, ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) ids.forEach(id => next.add(id));
      else ids.forEach(id => next.delete(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const applyBulkUpdate = async (updates) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    // Optimistic
    setTasks(prev => prev.map(t => ids.includes(t.task_id) ? { ...t, ...updates } : t));
    try {
      await axios.post(`${API}/projects/${projectId}/tasks/bulk-update`, { task_ids: ids, updates }, h());
      // Refresh to pick up server-side enrichment.
      fetchTasks();
    } catch (err) {
      console.error('[bulk-update]', err);
      fetchTasks();
    } finally {
      setBulkBusy(false);
    }
  };
  const applyBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!(await confirm({
      title: `Delete ${ids.length} task${ids.length > 1 ? 's' : ''}?`,
      description: 'This action cannot be undone. Comments, history, and attachments will be removed with each task.',
      confirmText: `Delete ${ids.length}`,
      variant: 'destructive',
    }))) return;
    setBulkBusy(true);
    setTasks(prev => prev.filter(t => !ids.includes(t.task_id)));
    clearSelection();
    try {
      await axios.post(`${API}/projects/${projectId}/tasks/bulk-delete`, { task_ids: ids }, h());
    } catch (err) {
      console.error('[bulk-delete]', err);
    } finally {
      setBulkBusy(false);
      fetchTasks();
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="project-board-page">
      <div className="border-b border-zinc-200 flex-shrink-0">
        <div className="px-6 pt-4 pb-2">
          {project?.client && (
            <div
              className="flex items-center gap-2.5 mb-2 -mx-6 px-6 py-2 border-l-4"
              style={{ borderColor: project.client.brand_color || '#4f46e5', backgroundColor: `${(project.client.brand_color || '#4f46e5')}0d` }}
              data-testid="board-client-strip"
            >
              <ClientLogo client={project.client} size={28} rounded="rounded-md" />
              <div className="leading-tight">
                <span className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold block">Client workspace</span>
                <span className="text-[13px] font-semibold text-zinc-800" data-testid="board-client-name">{project.client.name}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-1">
            <button onClick={() => navigate('/projects')} className="hover:text-zinc-600 transition-colors">Projects</button>
            <span>/</span><span className="text-zinc-600">{project?.name}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {project?.client ? (
                <ClientLogo client={project.client} size={28} rounded="rounded-lg" />
              ) : (
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[11px] font-bold">{project?.key?.charAt(0) || '?'}</div>
              )}
              <h1 className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">{project?.name || 'Loading...'}</h1>
              {project?.key && <Badge className="bg-zinc-100 text-zinc-500 border border-zinc-200 text-[10px] font-mono">{project.key}</Badge>}
              {activeTemplateId && (() => {
                const meta = TEMPLATE_META[activeTemplateId];
                const Icon = meta.icon;
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="template-badge"
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold cursor-pointer hover:shadow-sm transition-all ${meta.cls}`}
                        title="See what this template includes">
                        <Icon size={11} weight="duotone" /> {meta.label}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="start" className="w-80 p-4" data-testid="template-whats-included">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.cls} border-0`}>
                          <Icon size={16} weight="duotone" />
                        </div>
                        <div>
                          <div className="font-['Outfit'] text-[14px] font-bold text-zinc-900 leading-tight">{activeTemplate?.name || meta.label} template</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">{activeTemplate?.tagline || 'Pre-configured setup'}</div>
                        </div>
                      </div>
                      {activeTemplate?.description && (
                        <p className="text-[12px] text-zinc-600 leading-snug mb-3">{activeTemplate.description}</p>
                      )}
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mb-1.5 flex items-center gap-1"><Check size={10} weight="bold" /> What's included</div>
                      <div className="space-y-2 text-[12px]">
                        {activeTemplate?.custom_statuses?.length > 0 && (
                          <div>
                            <div className="text-zinc-500 text-[11px] mb-1">Extra board columns:</div>
                            <div className="flex flex-wrap gap-1">
                              {activeTemplate.custom_statuses.map(s => (
                                <span key={s.id} className="inline-flex items-center gap-1 text-[11px] text-zinc-700 bg-zinc-50 border border-zinc-100 px-2 py-0.5 rounded">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                                  {s.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {activeTemplate?.custom_fields?.length > 0 && (
                          <div>
                            <div className="text-zinc-500 text-[11px] mb-1">Custom fields (shown in task detail):</div>
                            <div className="flex flex-wrap gap-1">
                              {activeTemplate.custom_fields.map((f, i) => (
                                <span key={i} className="inline-flex items-center text-[11px] text-zinc-700 bg-zinc-50 border border-zinc-100 px-2 py-0.5 rounded">
                                  {f.name}
                                  <span className="text-zinc-400 ml-1">· {f.type}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(!activeTemplate?.custom_statuses?.length && !activeTemplate?.custom_fields?.length) && (
                          <p className="text-[11px] text-zinc-400 italic">No extra statuses or fields — a clean starting point.</p>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              })()}
              <Star size={16} className="text-zinc-300 hover:text-amber-400 cursor-pointer transition-colors" />
              {canEdit && (
                <Button data-testid="create-task-button" onClick={() => { setCreateDefaultStatus('todo'); setShowCreateTask(true); }} size="sm" className="h-7 ml-1 bg-blue-600 hover:bg-blue-700 text-white text-[11px] shadow-sm">
                  <Plus size={12} className="mr-1" /> Create Item
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1.5 mr-2">
                {assigneeAvatars.map((a, i) => {
                  const s = getAvatarStyle(a.id || a.name);
                  return (
                    <Avatar key={a.id} className="h-6 w-6 ring-2 ring-white" style={{ zIndex: 10 - i }} title={a.name}>
                      <AvatarImage src={a.avatar} />
                      <AvatarFallback className={`${s.bg} text-white text-[9px] font-bold`}>{getInitials(a.name)}</AvatarFallback>
                    </Avatar>
                  );
                })}
                {assigneeIds.length > 4 && <div className="h-6 w-6 rounded-full bg-zinc-200 ring-2 ring-white flex items-center justify-center text-[9px] font-bold text-zinc-500">+{assigneeIds.length - 4}</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 flex items-center justify-between">
          <div className="flex items-center gap-0">
            {[
              { id: 'summary', label: 'Summary', icon: ChartBar },
              { id: 'backlog', label: 'Backlog', icon: Lightning },
              { id: 'kanban', label: 'Board', icon: Kanban },
              { id: 'list', label: 'List', icon: ListBullets },
              { id: 'timeline', label: 'Timeline', icon: GitBranch, flagKey: 'timeline' },
              { id: 'docs', label: 'Docs', icon: BookOpen, flagKey: 'docs' },
              { id: 'whiteboard', label: 'Whiteboard', icon: PencilSimple, flagKey: 'whiteboard' },
            ]
              // Hide tabs when admin has explicitly disabled the feature for this org.
              // For plan-locked features we keep the tab visible so users discover them
              // and click → see the upgrade prompt.
              .filter(tab => !tab.flagKey || flagSource?.(tab.flagKey) !== 'admin_disabled')
              .map(tab => (
              <button key={tab.id} data-testid={`view-${tab.id}`} onClick={() => setView(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 transition-colors duration-150
                  ${view === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'}`}>
                <tab.icon size={14} /> {tab.label}
              </button>
            ))}
          </div>
          {/* Search + filters only make sense on the task-grid views (Board /
              List). Summary and Backlog have their own slicing controls. */}
          {(view === 'kanban' || view === 'list') && (
          <div className="flex items-center gap-2 pb-1">
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search tasks..." className="h-7 w-36 pl-7 text-[11px] border-zinc-200 focus:w-48 transition-all" data-testid="board-search" />
            </div>
            {members.length > 0 && (
              <>
                <div className="w-px h-5 bg-zinc-200 mx-0.5" />
                <AssigneeQuickFilter members={members} selected={filterAssignees} onToggle={toggleArrVal(setFilterAssignees)} onClear={() => setFilterAssignees([])} />
                <div className="w-px h-5 bg-zinc-200 mx-0.5" />
              </>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className={`h-7 text-[11px] ${activeFilterCount > 0 ? 'bg-blue-50 text-blue-600' : 'text-zinc-500'}`} data-testid="toggle-filters">
                  <FunnelSimple size={13} className="mr-1" /> Filter {activeFilterCount > 0 && <span className="ml-1 bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]">{activeFilterCount}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-0 overflow-hidden shadow-2xl border-zinc-200">
                <div className="px-3 py-2.5 border-b border-zinc-100 bg-gradient-to-br from-zinc-50 to-white flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-500">Filter board</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">Tick values · combined across groups</p>
                  </div>
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-testid="quick-filter-clear"><X size={10} /> Clear</button>
                  )}
                </div>
                <div className="p-3 space-y-3 max-h-[360px] overflow-y-auto">
                  <FilterGroup title="Status">
                    {allStatuses.map(s => (
                      <CheckRow key={s.value} testid={`filter-status-${s.value}`} checked={filterStatuses.includes(s.value)} onClick={() => toggleArrVal(setFilterStatuses)(s.value)} label={s.label} />
                    ))}
                  </FilterGroup>
                  <FilterGroup title="Type">
                    {[{ id: 'task', label: 'Task' }, { id: 'story', label: 'Story' }, { id: 'bug', label: 'Bug' }].map(t => (
                      <CheckRow key={t.id} testid={`filter-type-${t.id}`} checked={filterTypes.includes(t.id)} onClick={() => toggleArrVal(setFilterTypes)(t.id)} label={<span className="flex items-center gap-1.5"><WorkTypeIcon type={t.id} size={12} /> {t.label}</span>} />
                    ))}
                  </FilterGroup>
                  <FilterGroup title="Priority">
                    {[{ id: 'critical', label: 'Critical' }, { id: 'high', label: 'High' }, { id: 'medium', label: 'Medium' }, { id: 'low', label: 'Low' }].map(p => (
                      <CheckRow key={p.id} testid={`filter-priority-${p.id}`} checked={filterPriorities.includes(p.id)} onClick={() => toggleArrVal(setFilterPriorities)(p.id)} label={p.label} />
                    ))}
                  </FilterGroup>
                  {boardTags.length > 0 && (
                    <FilterGroup title="Tags">
                      {boardTags.map(tag => (
                        <CheckRow key={tag} testid={`filter-tag-${tag}`} checked={filterTags.includes(tag)} onClick={() => toggleArrVal(setFilterTags)(tag)} label={tag} />
                      ))}
                    </FilterGroup>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <CustomFiltersMenu
              projectId={projectId}
              filters={savedFilters}
              appliedFilterIds={appliedFilterIds}
              onToggle={toggleAppliedFilter}
              onClearAll={() => setAppliedFilterIds([])}
            />
            {canManageProject && (
              <Button variant="ghost" size="sm" onClick={() => { if (csvUnlocked) setShowImportExport(true); else navigate('/billing'); }} className="h-7 text-[11px] text-zinc-500" data-testid="import-export-btn">
                <DownloadSimple size={13} className="mr-1" /> Import / Export
                {!csvUnlocked && <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700" data-testid="import-export-lock">🔒 Pro</span>}
              </Button>
            )}
          </div>
          )}
        </div>

      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {/* Bulk-actions bar — shows above any view when selection is non-empty.
            Selection state currently flows from the List view; future iterations
            can wire Kanban cards into the same selectedIds set. */}
        {canEdit && selectedIds.size > 0 && (
          <BulkActionsBar
            selectedIds={Array.from(selectedIds)}
            onClear={clearSelection}
            onApplyUpdate={applyBulkUpdate}
            onApplyDelete={applyBulkDelete}
            members={members}
            customStatuses={project?.custom_statuses}
            sprints={projectSprints}
            busy={bulkBusy}
          />
        )}
        {view === 'kanban' && (
          <div className="px-6 pt-3 flex-shrink-0" data-testid="board-sprint-context">
            {activeSprint ? (
              <div className="flex items-center gap-2 text-[11px] text-zinc-500 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span>Showing <strong className="text-emerald-700">{activeSprint.name}</strong> · only items pulled into the active sprint appear on the board.</span>
                <button onClick={() => setView('backlog')} className="ml-auto font-semibold text-emerald-700 hover:text-emerald-800">View Backlog →</button>
              </div>
            ) : projectSprints.length > 0 ? (
              <div className="flex items-center gap-2 text-[11px] text-zinc-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-1.5" data-testid="board-no-active-sprint">
                <span>No active sprint. New tickets sit in the Backlog until you start a sprint.</span>
                <button onClick={() => setView('backlog')} className="ml-auto font-semibold text-amber-700 hover:text-amber-800">Open Backlog →</button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[11px] text-zinc-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-1.5" data-testid="board-no-sprints-yet">
                <span>This project has no sprints yet. Tickets stay in the Backlog until a sprint is created and started.</span>
                <button onClick={() => setView('backlog')} className="ml-auto font-semibold text-amber-700 hover:text-amber-800">Create a sprint →</button>
              </div>
            )}
          </div>
        )}
        {view === 'summary' ? (
          <ProjectOverview projectId={projectId} />
        ) : view === 'docs' ? (
          docsUnlocked ? (
            <PagesView projectId={projectId} project={project} members={members} canEdit={canEdit} />
          ) : (
            <UpgradeLockCard
              mode={flagSource?.('docs') === 'admin_disabled' ? 'admin_disabled' : 'plan_locked'}
              title={flagSource?.('docs') === 'admin_disabled' ? 'Docs disabled' : 'Docs is a Business feature'}
              description={flagSource?.('docs') === 'admin_disabled'
                ? 'The Docs feature has been disabled for your workspace. Contact your administrator or our support team if you need access.'
                : 'Capture meeting notes, PRDs and decision logs alongside your tasks. Upgrade to Business to unlock unlimited project Docs.'}
              featureName="docs"
              testId="docs-upgrade-lock"
            />
          )
        ) : view === 'whiteboard' ? (
          whiteboardUnlocked ? (
            <WhiteboardView projectId={projectId} canEdit={canEdit} />
          ) : (
            <UpgradeLockCard
              mode={flagSource?.('whiteboard') === 'admin_disabled' ? 'admin_disabled' : 'plan_locked'}
              title={flagSource?.('whiteboard') === 'admin_disabled' ? 'Whiteboard disabled' : 'Whiteboard is a Business feature'}
              description={flagSource?.('whiteboard') === 'admin_disabled'
                ? 'The Whiteboard feature has been disabled for your workspace. Contact your administrator or our support team if you need access.'
                : 'Diagram architecture, sprint flows and brainstorms with our Lucidchart-style canvas. Upgrade to Business to unlock unlimited boards.'}
              featureName="whiteboard"
              testId="whiteboard-upgrade-lock"
            />
          )
        ) : view === 'timeline' ? (
          timelineUnlocked ? (
            <TimelineView
              tasks={filteredTasks}
              sprints={projectSprints}
              members={members}
              links={projectLinks}
              onTaskClick={setSelectedTask}
              onTaskDateChange={canEdit ? async (taskId, dates) => {
                // Optimistic + server-truth merge (matches handleTaskFieldUpdate
                // pattern used elsewhere on this page). The Timeline bar shows
                // the new position instantly; on error we silently revert and
                // refetch — the inline preview already moved back.
                try {
                  await handleTaskFieldUpdate(taskId, dates);
                  toast.success('Date updated');
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Failed to update date');
                }
              } : undefined}
            />
          ) : (
            <UpgradeLockCard
              mode={flagSource?.('timeline') === 'admin_disabled' ? 'admin_disabled' : 'plan_locked'}
              title={flagSource?.('timeline') === 'admin_disabled' ? 'Timeline disabled' : 'Timeline is unavailable'}
              featureName="timeline"
              testId="timeline-upgrade-lock"
            />
          )
        ) : view === 'kanban' ? (
          <KanbanBoard tasks={filteredTasks} columns={project?.custom_statuses} statusOrder={project?.status_order} project={project} customFields={project?.custom_fields} members={members} onStatusChange={handleTaskStatusUpdate} onReorderTasks={canEdit ? handleReorder : undefined} onReorderColumns={canEdit ? handleReorderColumns : undefined} onTaskUpdate={handleTaskFieldUpdate} onTaskClick={setSelectedTask} onOpenFullScreen={(taskId) => navigate(`/tasks/${taskId}`)} getMemberName={getMemberName} getMemberAvatar={getMemberAvatar} canEdit={canEdit} onQuickAdd={handleQuickAdd} onAddColumn={handleAddColumn} onDeleteColumn={handleDeleteColumn} onRenameColumn={canEdit ? handleRenameColumn : undefined} selectedTaskId={selectedTask?.task_id} />
        ) : view === 'backlog' ? (
          <BacklogView projectId={projectId} project={project} members={members} canEdit={canEdit} canManageProject={canManageProject} onTaskClick={setSelectedTask} onOpenFullScreen={(taskId) => navigate(`/tasks/${taskId}`)} onTaskChanged={fetchTasks} openCreateTask={(prefill) => { setCreateDefaultStatus('todo'); setCreateSprintId(prefill?.sprint_id ?? undefined); setShowCreateTask(true); }} refreshSignal={taskListVersion} />
        ) : (
          <TaskListView tasks={filteredTasks} projectId={projectId} onTaskClick={setSelectedTask} getMemberName={getMemberName} getMemberAvatar={getMemberAvatar} onReorder={canEdit ? handleReorder : undefined} onTaskUpdate={handleTaskFieldUpdate} members={members} customFields={project?.custom_fields} customStatuses={project?.custom_statuses} canEdit={canEdit}
            selectedIds={canEdit ? selectedIds : undefined}
            onToggleSelect={canEdit ? toggleSelect : undefined}
            onToggleSelectAll={canEdit ? toggleSelectAll : undefined}
            activeTaskId={selectedTask?.task_id}
          />
        )}
      </div>

      <TaskSheet task={selectedTask} open={!!selectedTask} onClose={() => setSelectedTask(null)} onUpdated={async () => {
        const fresh = await fetchTasks();
        // Re-sync selectedTask from the freshly fetched list so inline edits
        // (title, status, custom fields) propagate to the open drawer without a manual reload.
        if (selectedTask && Array.isArray(fresh)) {
          const match = fresh.find(t => t.task_id === selectedTask.task_id);
          if (match) setSelectedTask(match);
        }
      }} members={members} canEdit={canEdit} customFields={project?.custom_fields} fieldLayouts={project?.field_layouts} project={project} allTasks={tasks} onTaskClick={setSelectedTask} />
      <CreateTaskDialog open={showCreateTask} onClose={() => { setShowCreateTask(false); setCreateSprintId(undefined); }} projectId={projectId} project={project} sprints={projectSprints} members={members} defaultStatus={createDefaultStatus} defaultSprintId={createSprintId} onCreated={() => { setShowCreateTask(false); setCreateSprintId(undefined); fetchTasks(); bumpTaskList(); }} />
      <ImportExportTasksDialog open={showImportExport} onClose={() => setShowImportExport(false)} projectId={projectId} project={project} activeSprint={activeSprint} onImported={() => { fetchTasks(); bumpTaskList(); }} />
    </div>
  );
}
